import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ContactSheet from "@/components/ContactSheet";
import BiotechVisualization from "@/components/BiotechVisualization";

const CONTACT_EMAIL = "Sophiainfinityinfo@gmail.com";
const LINKEDIN_URL = "https://www.linkedin.com/in/ravenne-dame-09072b413";
const PRIMARY_INQUIRY = "Request Private R&D Pilot Formulation Allocation";

// ⬇︎  Paste your GoFundMe URL here when ready (e.g. "https://www.gofundme.com/f/your-campaign").
// Leave as empty string ("") to show a graceful "Coming Soon" state.
const GOFUNDME_URL = "https://www.gofundme.com/u/e782d560-1a39-472c-97ff-ea25fa30b3df";

const MILESTONES = [
  {
    id: "01",
    tag: "Launch Phase · Completed",
    title: "Pilot formulation packaged & dispatched.",
    body: "Our first custom 4-ounce topical formulation pilot project has been officially packaged in bio-photonic violet glass and dispatched via three-day shipping to a prominent private contract client for immediate market validation. Tracking data confirms arrival is scheduled on or before this Friday.",
  },
  {
    id: "02",
    tag: "Pre-Seed Outreach · Hard-Locked",
    title: "Numerous verified applications, live across the network.",
    body: "Our pre-seed brand partnership outreach is successfully maximized and hard-locked. Numerous applications are actively tracking across our performance casting networks alongside a specialized anonymous pipeline for mass-market retail connections.",
  },
  {
    id: "03",
    tag: "Executive Alignment · Established",
    title: "Locked briefing with our global broker network.",
    body: "International executive consulting alignment established. Management is entering a locked briefing call this Friday morning with our global broker network to review technical B2B scaling nodes — including AI automation, SEO, web development, and digital marketing structures.",
  },
];

const PRODUCT_CARDS = [
  {
    id: "01",
    category: "Modular Wellness Formulations",
    name: "The Lecture of Life",
    label: "Utility Patent-Pending · Filed with the USPTO",
    description:
      "A 13-element functional wellness architecture utilizing precise extraction ratios of Shilajit, Astragalus, and Soursop Leaf. Operating under a strict 3-on / 4-off cyclical dosing protocol engineered to mitigate systemic and environmental stress at a cellular level — and stabilize deep neural pathways.",
    status: "In Private Development Track",
  },
];

const UNPATENTED_INVENTIONS = [
  {
    category: "Longevity Science",
    name: "Zombie Cell Research",
    note: "Active investigation into senescent (zombie) cell clearance and cellular age-reversal pathways.",
  },
  {
    category: "Sleep Architecture",
    name: "Restorative Bed Systems",
    note: "Multi-layer sleep environments tuned for nervous-system downshift and deep cellular recovery.",
  },
  {
    category: "Functional Nutrition",
    name: "Botanical Smoothie Protocols",
    note: "Daily liquid formulations balancing adaptogens, micronutrients, and synergy ratios.",
  },
  {
    category: "Botanical Infusions",
    name: "Proprietary Tea Lines",
    note: "Curated herbal blends mapped to circadian cycles and targeted wellness outcomes.",
  },
  {
    category: "Oral Biome Care",
    name: "Mineral Toothpaste",
    note: "Non-fluoride dentifrice engineered to protect and rebuild the natural oral microbiome.",
  },
  {
    category: "Adaptive Apparel",
    name: "Functional Wellness Socks",
    note: "Therapeutic hosiery integrating compression and meridian-aligned support zones.",
  },
];

const INTAKE_OPTIONS = [
  "Request Private R&D Pilot Formulation Allocation",
  "Explore Institutional Licensing & Leasing Contracts",
  "Corporate Capitalization & Seed Funding Contributions",
];

export default function Landing() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  return (
    <div
      data-testid="landing-root"
      className="relative min-h-screen w-full bg-[#030303] text-white font-body overflow-x-hidden"
    >
      {/* Subtle noise grain */}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-0 pointer-events-none opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220' viewBox='0 0 220 220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Top bar */}
      <header
        data-testid="top-bar"
        className={`relative z-10 w-full border-b border-[#C0C0C0]/25 transition-opacity duration-1000 ${
          mounted ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-6 flex items-center justify-between">
          <div
            data-testid="brand-mark"
            className="text-xs uppercase tracking-[0.35em] text-[#E5E4E2]/70 font-body"
          >
            Sophia Infinity
          </div>
          <div className="hidden sm:block text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-body">
            Stealth Mode · 2026
          </div>
        </div>
      </header>

      {/* HERO */}
      <main className="relative z-10">
        <section
          data-testid="hero-section"
          className="relative min-h-[92vh] flex items-center px-6 sm:px-12 lg:px-20 pt-20 pb-24"
        >
          {/* Mobile background visualization */}
          <div
            aria-hidden="true"
            className="lg:hidden absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.08] z-0"
          >
            <BiotechVisualization className="w-[140%] max-w-none" />
          </div>
          <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10">
            <div className="lg:col-span-7 text-left w-full">
              {/* Top label */}
              <div
                data-testid="top-label"
                className={`flex items-center justify-start gap-4 mb-12 transition-all duration-1000 delay-100 ${
                  mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
                }`}
              >
                <span className="h-px w-10 bg-[#D4AF37]/60" />
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">
                  Secure Staging Environment
                </span>
              </div>

              {/* H1 */}
              <h1
                data-testid="hero-headline"
                className={`font-heading font-semibold text-white tracking-[-0.03em] leading-[1.02] text-4xl sm:text-5xl lg:text-[3.5rem] transition-all duration-1000 delay-200 ${
                  mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
              >
                Ancient Botanical Intelligence Scaled for{" "}
                <span className="text-[#D4AF37] font-medium">
                  Modern Cellular Longevity
                </span>
                .
              </h1>

              {/* Divider */}
              <div
                aria-hidden="true"
                className={`my-10 h-px w-24 bg-[#C0C0C0]/60 transition-opacity duration-1000 delay-300 ${
                  mounted ? "opacity-100" : "opacity-0"
                }`}
              />

              {/* Subheadline */}
              <p
                data-testid="hero-subheadline"
                className={`text-[#E5E4E2]/85 font-body font-light text-lg sm:text-xl lg:text-[1.35rem] leading-[1.7] max-w-2xl transition-all duration-1000 delay-300 ${
                  mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
              >
                Sophia Infinity bridges intuitive energetic architecture with rigorous
                research to deploy proprietary,{" "}
                <span className="text-white">utility patent-pending</span> physical
                formulations and advanced digital tracking networks. We specialize in
                locking down robust B2B corporate lease frameworks to scale premium
                lifestyle assets onto global enterprise pipelines.
              </p>

              {/* Corporate Compliance Note */}
              <div
                data-testid="compliance-note"
                className={`mt-10 max-w-2xl border-y border-[#C0C0C0]/30 py-5 transition-all duration-1000 delay-400 ${
                  mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
              >
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/80 mb-2">
                  ⟢ Corporate Compliance Notice
                </div>
                <p className="text-[#E5E4E2]/55 font-body text-[13px] sm:text-sm leading-[1.7]">
                  Sophia Infinity operates exclusively as a private independent research
                  and development framework. We do not hold public commercial retail
                  licenses, and we do not offer public consumer shopping options. Select
                  partnerships currently open.
                </p>
                <p className="text-[#E5E4E2]/55 font-body text-[13px] sm:text-sm leading-[1.7] mt-3">
                  Multi-product consumer delivery lanes and verified formulation data
                  briefs are actively deploying to selective contract partners and
                  strategic alliance nodes ahead of our upcoming public initialization
                  phase.
                </p>
              </div>

              {/* CTA Box */}
              <div
                data-testid="cta-box"
                className={`mt-12 flex flex-col items-start gap-6 w-full transition-all duration-1000 delay-500 ${
                  mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
              >
                <div className="flex flex-col sm:flex-row items-stretch gap-4 sm:gap-5 w-full max-w-2xl">
                  <a
                    href="#app-portal"
                    data-testid="request-access-cta"
                    className="group inline-flex flex-1 items-center justify-center gap-3 whitespace-nowrap bg-white text-black hover:bg-[#D4AF37] hover:text-black transition-colors duration-500 text-xs sm:text-sm uppercase tracking-[0.3em] font-body px-6 py-5 rounded-none"
                  >
                    Enter App Portal
                    <span
                      aria-hidden="true"
                      className="inline-block translate-x-0 group-hover:translate-x-1 transition-transform duration-500"
                    >
                      →
                    </span>
                  </a>

                  {/* Support / Donate */}
                  {GOFUNDME_URL ? (
                    <a
                      data-testid="support-donate-cta"
                      href={GOFUNDME_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex flex-1 items-center justify-center gap-3 whitespace-nowrap border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors duration-500 text-xs sm:text-sm uppercase tracking-[0.3em] font-body px-6 py-5 rounded-none"
                    >
                      Support Our Mission
                      <span
                        aria-hidden="true"
                        className="inline-block translate-x-0 group-hover:translate-x-1 transition-transform duration-500"
                      >
                        ↗
                      </span>
                    </a>
                  ) : (
                    <button
                      type="button"
                      disabled
                      data-testid="support-donate-cta-pending"
                      title="GoFundMe link coming soon"
                      className="inline-flex flex-1 items-center justify-center gap-3 whitespace-nowrap border border-[#C0C0C0]/30 text-[#E5E4E2]/40 cursor-not-allowed text-xs sm:text-sm uppercase tracking-[0.3em] font-body px-6 py-5 rounded-none"
                    >
                      Support Our Mission
                      <span className="text-[10px] tracking-[0.3em] text-[#D4AF37]/70">
                        · Link Pending
                      </span>
                    </button>
                  )}
                </div>

                <div
                  data-testid="launch-target"
                  className="flex items-center gap-3 font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#E5E4E2]/55"
                >
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
                  Targeted Public Launch · June 2027
                </div>
              </div>
            </div>

            {/* Desktop biotech visualization (right side) */}
            <div
              className={`hidden lg:block lg:col-span-5 transition-opacity duration-1500 delay-500 ${
                mounted ? "opacity-100" : "opacity-0"
              }`}
            >
              <BiotechVisualization className="w-full" />
            </div>
          </div>
        </section>

        {/* Ecosystem Momentum & Active Validation */}
        <section
          data-testid="momentum-section"
          className="relative border-t border-[#C0C0C0]/25 px-6 sm:px-12 lg:px-20 py-24 sm:py-32"
        >
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mb-14">
              <div className="lg:col-span-6">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.5em] text-[#D4AF37] mb-6">
                  ⟢ Active Velocity
                </div>
                <h2
                  data-testid="momentum-title"
                  className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl text-white tracking-[-0.025em] leading-[1.02]"
                >
                  Ecosystem Momentum{" "}
                  <span className="text-[#D4AF37]">&amp; Active Validation</span>.
                </h2>
              </div>
              <div className="lg:col-span-6 lg:pt-3">
                <p className="text-[#E5E4E2]/70 text-base sm:text-lg leading-[1.75] max-w-xl">
                  Live milestone tracking from our private R&amp;D framework. Each node
                  represents a verified, dated operational signal — dispatched, locked, or
                  scheduled within the active window.
                </p>
              </div>
            </div>

            {/* Client Advisor Validation Holder — reserved placeholder */}
            <div
              data-testid="client-validation-holder"
              className="relative border border-[#D4AF37]/40 p-8 sm:p-12 lg:p-14 mb-20"
            >
              {/* Corner accents */}
              <span aria-hidden="true" className="absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-[#D4AF37]" />
              <span aria-hidden="true" className="absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-[#D4AF37]" />
              <span aria-hidden="true" className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-[#D4AF37]" />
              <span aria-hidden="true" className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-[#D4AF37]" />

              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-6">
                <div className="flex items-center gap-3">
                  <span className="inline-block h-2 w-2 rounded-full bg-[#D4AF37] animate-pulse" />
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.45em] text-[#D4AF37]">
                    Live Client Testimony Node
                  </span>
                </div>
                <span className="hidden sm:block flex-1 h-px bg-[#D4AF37]/25" />
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#E5E4E2]/45">
                  Status :: Awaiting Verification
                </span>
              </div>

              <p className="font-heading text-lg sm:text-xl lg:text-2xl text-white/90 font-light leading-[1.45] tracking-tight max-w-3xl">
                Standing by for active project delivery verification update — rolling in
                over the next week or two from our prominent private contract partner.
              </p>

              <div className="mt-6 font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#E5E4E2]/35">
                ⌁ Placement reserved — Sophia Infinity Advisory Board
              </div>
            </div>

            {/* Milestone Timeline */}
            <ol data-testid="milestone-list" className="relative">
              {MILESTONES.map((m, i) => (
                <li
                  key={m.id}
                  data-testid={`milestone-${m.id}`}
                  className="relative grid grid-cols-12 gap-4 sm:gap-8 pb-14 last:pb-0"
                >
                  {/* Marker + connector */}
                  <div className="col-span-2 sm:col-span-1 flex flex-col items-center">
                    <div className="relative z-10 w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center bg-[#030303]">
                      <span className="font-mono-tech text-[10px] tracking-wider text-[#D4AF37]">
                        {m.id}
                      </span>
                    </div>
                    {i < MILESTONES.length - 1 && (
                      <div
                        aria-hidden="true"
                        className="flex-1 w-px bg-gradient-to-b from-[#D4AF37]/45 via-[#C0C0C0]/50 to-[#C0C0C0]/10 mt-3 mb-1"
                      />
                    )}
                  </div>

                  {/* Content */}
                  <div className="col-span-10 sm:col-span-11">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
                      <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]/85">
                        {m.tag}
                      </span>
                    </div>
                    <h3 className="font-heading font-medium text-xl sm:text-2xl lg:text-[1.75rem] text-white leading-[1.2] tracking-[-0.015em] mb-4 max-w-3xl">
                      {m.title}
                    </h3>
                    <p className="text-[#E5E4E2]/75 font-body font-light text-base sm:text-lg leading-[1.75] max-w-3xl">
                      {m.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* SECTION 04 — Core Intellectual Property Architecture */}
        <section
          data-testid="ip-section"
          className="relative border-t border-[#C0C0C0]/25 px-6 sm:px-12 lg:px-20 py-24 sm:py-32"
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mb-16">
              <div className="lg:col-span-6">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.5em] text-[#D4AF37] mb-6">
                  ⟢ Section 04 · IP Architecture
                </div>
                <h2
                  data-testid="ip-title"
                  className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl text-white tracking-[-0.025em] leading-[1.05]"
                >
                  Core Intellectual Property{" "}
                  <span className="text-[#D4AF37]">Architecture</span>.
                </h2>
              </div>
              <div className="lg:col-span-6 lg:pt-3">
                <p className="text-[#E5E4E2]/70 text-base sm:text-lg leading-[1.75] max-w-xl">
                  Sophia Infinity manages a comprehensive pipeline of{" "}
                  <span className="text-white">26 total proprietary inventions</span>.
                  Our core matrices, compound ratios, and molecular designs are
                  protected trade secrets and are withheld from public exploratory
                  vetting until a Mutual NDA is executed.
                </p>
              </div>
            </div>

            {/* Product Cards */}
            <div className="grid grid-cols-1 gap-6 lg:gap-8 mb-20 max-w-3xl mx-auto">
              {PRODUCT_CARDS.map((c) => (
                <article
                  key={c.id}
                  data-testid={`product-card-${c.id}`}
                  className="relative border border-[#C0C0C0]/40 hover:border-[#D4AF37]/45 transition-colors duration-500 bg-gradient-to-b from-[#070707] to-[#020202] p-8 sm:p-10 flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="font-mono-tech text-[10px] tracking-[0.3em] text-[#D4AF37]/80">
                      ⟢ INVENTION {c.id}
                    </span>
                    <span className="h-px flex-1 bg-[#C0C0C0]/40" />
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#E5E4E2]/50">
                      {c.category}
                    </span>
                  </div>

                  <h3 className="font-heading font-medium text-2xl sm:text-[1.75rem] text-white tracking-[-0.02em] leading-[1.1] mb-3">
                    {c.name}
                  </h3>

                  {c.label && (
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-5">
                      ⌁ {c.label}
                    </div>
                  )}
                  {!c.label && <div className="mb-5" />}

                  <p className="text-[#E5E4E2]/75 font-body font-light text-base leading-[1.75] mb-8">
                    {c.description}
                  </p>

                  <div className="mt-auto pt-6 border-t border-[#C0C0C0]/40 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
                      <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#E5E4E2]/60">
                        Status
                      </span>
                    </div>
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] text-right">
                      {c.status}
                    </span>
                  </div>
                </article>
              ))}
            </div>

            {/* Unpatented Invention Catalog */}
            <div className="border-t border-[#C0C0C0]/40 pt-12">
              <div className="flex items-center gap-4 mb-8">
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">
                  ⟢ Active Invention Catalog · Pre-Patent Stage
                </span>
                <span className="h-px flex-1 bg-[#C0C0C0]/40" />
              </div>

              <p className="text-[#E5E4E2]/75 font-body font-light text-base sm:text-lg leading-[1.75] max-w-3xl mb-10">
                The cards below are a{" "}
                <span className="text-white">curated public selection</span> from a
                much larger active catalog spanning longevity science, sleep
                architecture, functional ingestibles, oral biome care, adaptive
                apparel, and more.{" "}
                <span className="text-white">
                  All concepts below — and many beyond what is shown here — are
                  proprietary and currently unpatented
                </span>{" "}
                — held in stealth development pending strategic USPTO filing.
              </p>

              <div
                data-testid="unpatented-invention-grid"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-12"
              >
                {UNPATENTED_INVENTIONS.map((inv, i) => (
                  <article
                    key={inv.name}
                    data-testid={`unpatented-${i + 1}`}
                    className="relative border border-[#C0C0C0]/40 hover:border-[#D4AF37]/40 transition-colors duration-500 bg-gradient-to-b from-[#070707] to-[#020202] p-6 sm:p-7 flex flex-col"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="font-mono-tech text-[10px] tracking-[0.3em] text-[#D4AF37]/80">
                        {String(i + 2).padStart(2, "0")}
                      </span>
                      <span className="h-px flex-1 bg-[#C0C0C0]/40" />
                      <span className="font-mono-tech text-[9px] uppercase tracking-[0.25em] text-[#E5E4E2]/55">
                        {inv.category}
                      </span>
                    </div>
                    <h3 className="font-heading font-medium text-xl sm:text-[1.4rem] text-white tracking-[-0.015em] leading-[1.15] mb-3">
                      {inv.name}
                    </h3>
                    <p className="text-[#E5E4E2]/70 font-body font-light text-sm leading-[1.7] mb-5">
                      {inv.note}
                    </p>
                    <div className="mt-auto pt-4 border-t border-[#C0C0C0]/30 flex items-center justify-between gap-3">
                      <span className="font-mono-tech text-[9px] uppercase tracking-[0.3em] text-[#E5E4E2]/55">
                        Status
                      </span>
                      <span className="font-mono-tech text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">
                        Unpatented · Stealth
                      </span>
                    </div>
                  </article>
                ))}

                {/* "+ More" tile */}
                <article
                  data-testid="unpatented-more"
                  className="relative border border-dashed border-[#D4AF37]/40 bg-[#C0C0C0]/[0.05] p-6 sm:p-7 flex flex-col justify-center items-center text-center"
                >
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3">
                    ⌁ Many More Withheld
                  </div>
                  <div className="font-heading text-lg sm:text-xl text-white font-medium tracking-[-0.015em] mb-2">
                    + Beyond Public Display
                  </div>
                  <p className="text-[#E5E4E2]/65 font-body text-sm leading-[1.65]">
                    The above is only a partial reveal. Numerous additional
                    proprietary inventions remain undisclosed — accessible under NDA
                    only.
                  </p>
                </article>
              </div>

              {/* Access notice */}
              <div className="relative border border-[#D4AF37]/35 p-6 sm:p-8 bg-[#C0C0C0]/[0.06]">
                <span aria-hidden="true" className="absolute -top-px -left-px h-2.5 w-2.5 border-t-2 border-l-2 border-[#D4AF37]" />
                <span aria-hidden="true" className="absolute -top-px -right-px h-2.5 w-2.5 border-t-2 border-r-2 border-[#D4AF37]" />
                <span aria-hidden="true" className="absolute -bottom-px -left-px h-2.5 w-2.5 border-b-2 border-l-2 border-[#D4AF37]" />
                <span aria-hidden="true" className="absolute -bottom-px -right-px h-2.5 w-2.5 border-b-2 border-r-2 border-[#D4AF37]" />
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-2">
                  ⌁ Trade-Secret Protocol
                </div>
                <p className="text-[#E5E4E2]/85 font-body text-base sm:text-lg leading-relaxed mb-4">
                  There is{" "}
                  <span className="text-white">far more than what appears above</span>{" "}
                  — the inventions shown here are only the ones being added to this
                  site. The broader catalog remains intentionally undisclosed.
                </p>
                <p className="text-[#E5E4E2]/75 font-body text-base sm:text-lg leading-relaxed">
                  All unpatented concepts are protected as{" "}
                  <span className="text-white">proprietary trade secrets</span>.
                  Detailed compound ratios, mechanical schematics, and clinical
                  frameworks require an Executed Mutual NDA for review.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Founder / Origin */}
        <section
          data-testid="founder-section"
          className="relative border-t border-[#C0C0C0]/25 px-6 sm:px-12 lg:px-20 py-24 sm:py-32"
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
              {/* Left: title block */}
              <div className="lg:col-span-5">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.5em] text-[#D4AF37] mb-6">
                  ⟢ Founder · Origin Record
                </div>
                <h2
                  data-testid="founder-title"
                  className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl text-white tracking-[-0.025em] leading-[1.05]"
                >
                  Driven by Vision.{" "}
                  <span className="text-[#D4AF37]">Anchored in Sovereign Truth</span>.
                </h2>

                {/* Founder identity card */}
                <div className="mt-10 border border-[#D4AF37]/30 p-6 sm:p-7 relative max-w-md">
                  <span aria-hidden="true" className="absolute -top-px -left-px h-2.5 w-2.5 border-t-2 border-l-2 border-[#D4AF37]" />
                  <span aria-hidden="true" className="absolute -top-px -right-px h-2.5 w-2.5 border-t-2 border-r-2 border-[#D4AF37]" />
                  <span aria-hidden="true" className="absolute -bottom-px -left-px h-2.5 w-2.5 border-b-2 border-l-2 border-[#D4AF37]" />
                  <span aria-hidden="true" className="absolute -bottom-px -right-px h-2.5 w-2.5 border-b-2 border-r-2 border-[#D4AF37]" />

                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3">
                    On Record
                  </div>
                  <div className="font-heading text-2xl sm:text-3xl text-white font-medium tracking-[-0.015em] leading-tight">
                    Ravenne Dame
                  </div>
                  <div className="mt-2 font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#E5E4E2]/55">
                    Albany, NY · Age 29
                  </div>
                  <ul className="mt-4 space-y-1.5 text-[#E5E4E2]/65 font-body text-sm leading-relaxed">
                    <li>— Founder &amp; Lead Inventor</li>
                    <li>— Independent Researcher</li>
                    <li>— Sole Asset Owner</li>
                    <li>— Mother of Five</li>
                  </ul>
                </div>
              </div>

              {/* Right: body copy */}
              <div className="lg:col-span-7 lg:pt-3">
                <div
                  data-testid="founder-body"
                  className="space-y-10 text-[#E5E4E2]/85 font-body font-light text-base sm:text-lg leading-[1.85] max-w-2xl"
                >
                  {/* Corporate Statement */}
                  <div className="space-y-4">
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/80">
                      ⌁ Corporate Statement
                    </div>
                    <p>
                      My name is{" "}
                      <span className="text-white font-normal">Ravenne Dame</span>. I am
                      a 29-year-old independent inventor, researcher, and visionary
                      operating out of{" "}
                      <span className="text-[#D4AF37]">Albany, New York</span>. My life
                      and work are anchored by an absolute devotion to my family as a
                      proud mother of five beautiful children: two daughters and one son
                      by my side, a cherished stepdaughter, and one precious angel baby
                      watching over our timeline from heaven.
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="h-px flex-1 bg-[#C0C0C0]/50" />
                    <span className="font-mono-tech text-[9px] uppercase tracking-[0.4em] text-[#D4AF37]/70">
                      ⌁ Awakening Timeline
                    </span>
                    <span className="h-px flex-1 bg-[#C0C0C0]/50" />
                  </div>

                  {/* Awakening Timeline */}
                  <div className="space-y-4">
                    <p>
                      My path as an independent operator was forged through extreme
                      personal survival, but the ultimate catalyst for my profound
                      spiritual awakening occurred in{" "}
                      <span className="text-[#D4AF37]">2024</span>. Following a
                      devastating stillbirth, my reality was pulled into a profound
                      spiritual darkness and intense grief. Yet, in that depth of
                      shattering loss, I refused to break.
                    </p>
                    <p>
                      Instead, I chose to draw closer to the absolute universal Creator,
                      allowing that raw pain to completely strip away mainstream
                      commercial illusions, clear historic cellular blocks, and
                      permanently realign my internal antenna. By{" "}
                      <span className="text-[#D4AF37]">2026</span>, this unyielding
                      resilience sparked a total transformation, allowing me to fully
                      activate my inner God spark and step into{" "}
                      <span className="text-white font-normal">
                        absolute spiritual and personal sovereignty
                      </span>
                      .
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="h-px flex-1 bg-[#C0C0C0]/50" />
                    <span className="font-mono-tech text-[9px] uppercase tracking-[0.4em] text-[#D4AF37]/70">
                      ⌁ R&amp;D Architecture
                    </span>
                    <span className="h-px flex-1 bg-[#C0C0C0]/50" />
                  </div>

                  {/* R&D Architecture */}
                  <div className="space-y-4">
                    <p>
                      Today, everything I architect comes from a place of direct sight
                      and absolute internal alignment. I intuitively design our advanced
                      physical formulations and recovery templates by meditating,
                      channeling, and focusing for 24 hours straight. I then
                      systematically utilize advanced Artificial Intelligence models,
                      clinical data metrics, and historical peer-reviewed scientific
                      literature to prove out the exact biochemical facts, cellular
                      absorption parameters, and physical metrics behind what I
                      experience spiritually to the material world.
                    </p>
                    <p>
                      I serve as the lead guardian of a high-value research and
                      development pipeline tracking{" "}
                      <span className="text-[#D4AF37]">
                        over 40 distinct architectural innovations
                      </span>{" "}
                      and{" "}
                      <span className="text-[#D4AF37]">
                        26 completed, non-patented inventions
                      </span>
                      . We bridge the gap between ancient botanical biotech, conscious
                      literature, and global manufacturing scalability.
                    </p>
                  </div>
                </div>

                {/* Footer mono signature */}
                <div className="mt-12 flex items-center gap-4 max-w-2xl">
                  <span className="h-px flex-1 bg-[#C0C0C0]/50" />
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#E5E4E2]/45">
                    λ Sophia Transmission · v0.13
                  </span>
                  <span className="h-px flex-1 bg-[#C0C0C0]/50" />
                </div>

                {/* Sanctuary page link */}
                <div className="mt-10">
                  <Link
                    to="/about-ravenne"
                    data-testid="read-sanctuary-link"
                    className="group inline-flex items-center gap-3 border border-[#D4AF37]/45 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors duration-500 text-xs uppercase tracking-[0.3em] font-body px-8 py-4 rounded-none"
                  >
                    Read Her Full Awakening
                    <span
                      aria-hidden="true"
                      className="inline-block translate-x-0 group-hover:translate-x-1 transition-transform duration-500"
                    >
                      →
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MODULE 04 — Crowdfunding Infrastructure & Regeneration Mission */}
        <section
          data-testid="crowdfunding-section"
          className="relative border-t border-[#C0C0C0]/25 px-6 sm:px-12 lg:px-20 py-24 sm:py-32"
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mb-14">
              <div className="lg:col-span-6">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.5em] text-[#D4AF37] mb-6">
                  ⟢ Regeneration Mission · Independent Track
                </div>
                <h2
                  data-testid="crowdfunding-title"
                  className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl text-white tracking-[-0.025em] leading-[1.05]"
                >
                  Fuel the Horizon:{" "}
                  <span className="text-[#D4AF37]">
                    A Vision to Optimize Human Longevity
                  </span>
                  .
                </h2>
              </div>
              <div className="lg:col-span-6 lg:pt-3">
                <p className="text-[#E5E4E2]/70 text-base sm:text-lg leading-[1.75] max-w-xl">
                  A zero-dollar bootstrap. A sovereign refusal of dilutive venture
                  capital. A call to conscious visionaries, sovereign donors, and
                  community supporters worldwide.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-12">
              {/* Macro Vision Narrative */}
              <div
                data-testid="macro-vision-block"
                className="relative border border-[#C0C0C0]/40 p-8 sm:p-10 bg-gradient-to-b from-[#070707] to-[#020202]"
              >
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-5">
                  ⟢ Macro Vision Narrative
                </div>
                <h3 className="font-heading font-medium text-xl sm:text-2xl lg:text-[1.6rem] text-white tracking-[-0.015em] leading-[1.2] mb-5">
                  Raw tools, clean botanical inputs, and{" "}
                  <span className="text-[#D4AF37]">sovereign technologies</span>.
                </h3>
                <p className="text-[#E5E4E2]/75 font-body font-light text-base leading-[1.75]">
                  At Sophia Infinity, our ultimate macro goal is to provide humanity
                  with the raw tools, clean botanical inputs, and sovereign
                  technologies required to optimize biological health, unlock inherent
                  spiritual capabilities, and maximize the natural lifespan of the
                  human vehicle. We believe that by clearing modern calcification
                  matrices and returning to pure source-consciousness parameters, the
                  body can achieve unprecedented levels of systemic rejuvenation.
                </p>
              </div>

              {/* Capital Call Parameter */}
              <div
                data-testid="capital-call-block"
                className="relative border border-[#C0C0C0]/40 p-8 sm:p-10 bg-gradient-to-b from-[#070707] to-[#020202]"
              >
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-5">
                  ⟢ Capital Call Parameter
                </div>
                <h3 className="font-heading font-medium text-xl sm:text-2xl lg:text-[1.6rem] text-white tracking-[-0.015em] leading-[1.2] mb-5">
                  Zero-dollar bootstrap.{" "}
                  <span className="text-[#D4AF37]">
                    Undiluted by venture capital
                  </span>
                  .
                </h3>
                <p className="text-[#E5E4E2]/75 font-body font-light text-base leading-[1.75]">
                  To achieve this paradigm-shifting goal for the world on an
                  independent track, we are benchmarking our entire 26-invention
                  ecosystem on a strict zero-dollar bootstrap budget. We choose to
                  remain entirely independent of traditional dilutive venture funds
                  that seek to corporate-censor or restrict our formulations. We are
                  calling upon conscious visionaries, sovereign donors, and community
                  supporters worldwide to back our active infrastructure campaign.
                  Your direct contributions fuel our technical documentation tracks,
                  clear local regulatory filing requirements, and expand our
                  manufacturing capacity to deliver these restorative properties to
                  the open world.
                </p>
              </div>
            </div>

            {/* Crowdfunding Action */}
            <div className="relative border border-[#D4AF37]/40 bg-[#C0C0C0]/[0.08] p-8 sm:p-10">
              <span aria-hidden="true" className="absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-[#D4AF37]" />
              <span aria-hidden="true" className="absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-[#D4AF37]" />
              <span aria-hidden="true" className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-[#D4AF37]" />
              <span aria-hidden="true" className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-[#D4AF37]" />

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3">
                    ⌁ Active Infrastructure Campaign
                  </div>
                  <h3 className="font-heading font-medium text-2xl sm:text-3xl text-white tracking-[-0.015em] leading-[1.2]">
                    Directly fuel technical documentation, regulatory filings, and
                    manufacturing capacity.
                  </h3>
                </div>
                <div className="lg:col-span-5">
                  {GOFUNDME_URL ? (
                    <a
                      data-testid="crowdfund-donate-cta"
                      href={GOFUNDME_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex w-full items-center justify-center gap-3 whitespace-nowrap bg-white text-black hover:bg-[#D4AF37] hover:text-black transition-colors duration-500 text-xs sm:text-sm uppercase tracking-[0.3em] font-body px-6 py-5 rounded-none"
                    >
                      Donate to Our Infrastructure Campaign
                      <span
                        aria-hidden="true"
                        className="inline-block translate-x-0 group-hover:translate-x-1 transition-transform duration-500"
                      >
                        ↗
                      </span>
                    </a>
                  ) : (
                    <button
                      type="button"
                      disabled
                      data-testid="crowdfund-donate-cta-pending"
                      className="inline-flex w-full items-center justify-center gap-3 whitespace-nowrap border border-[#C0C0C0]/30 text-[#E5E4E2]/40 cursor-not-allowed text-xs sm:text-sm uppercase tracking-[0.3em] font-body px-6 py-5 rounded-none"
                    >
                      Donate — Link Pending
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MODULE 05 — The Digital Portal · Tree of Life · Sovereign Node App */}
        <section
          id="app-portal"
          data-testid="portal-section"
          className="relative border-t border-[#C0C0C0]/25 px-6 sm:px-12 lg:px-20 py-24 sm:py-32 scroll-mt-24"
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mb-14">
              <div className="lg:col-span-6">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.5em] text-[#D4AF37] mb-6">
                  ⟢ Module 05 · The Digital Portal
                </div>
                <h2
                  data-testid="portal-title"
                  className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl text-white tracking-[-0.025em] leading-[1.05]"
                >
                  Tree of Life ·{" "}
                  <span className="text-[#D4AF37]">
                    Abraxas Mindset &amp; Sovereign Node App
                  </span>
                  .
                </h2>
              </div>
              <div className="lg:col-span-6 lg:pt-3">
                <p className="text-[#E5E4E2]/70 text-base sm:text-lg leading-[1.75] max-w-xl">
                  The forthcoming operating layer that unifies our botanical formulation
                  intelligence, Abraxas mindset architecture, and encrypted personal
                  longevity metrics into a single conscious sovereign node.
                </p>
              </div>
            </div>

            <div className="relative border border-[#D4AF37]/35 bg-gradient-to-b from-[#070707] to-[#020202] p-8 sm:p-12">
              <span aria-hidden="true" className="absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-[#D4AF37]" />
              <span aria-hidden="true" className="absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-[#D4AF37]" />
              <span aria-hidden="true" className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-[#D4AF37]" />
              <span aria-hidden="true" className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-[#D4AF37]" />

              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-8">
                <div className="flex items-center gap-3">
                  <span className="inline-block h-2 w-2 rounded-full bg-[#D4AF37] animate-pulse" />
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.45em] text-[#D4AF37]">
                    Portal Status
                  </span>
                </div>
                <span className="hidden sm:block flex-1 h-px bg-[#D4AF37]/25" />
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#E5E4E2]/55">
                  In Structural Build · Public Initialization Approaching
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-10">
                {[
                  {
                    tag: "Layer 01",
                    name: "Tree of Life Interface",
                    body: "Navigable sovereign map connecting each botanical protocol to its energetic node.",
                  },
                  {
                    tag: "Layer 02",
                    name: "Abraxas Mindset Track",
                    body: "Structured mental architecture, transmission logs, and gnostic training modules.",
                  },
                  {
                    tag: "Layer 03",
                    name: "Encrypted Longevity Vault",
                    body: "Personal biometric ledger — private, encrypted, and portable across devices.",
                  },
                ].map((layer) => (
                  <div
                    key={layer.tag}
                    className="border border-[#C0C0C0]/40 p-5 sm:p-6 bg-[#050505]"
                  >
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]/85 mb-3">
                      ⟢ {layer.tag}
                    </div>
                    <div className="font-heading text-lg sm:text-xl text-white font-medium tracking-[-0.015em] leading-tight mb-3">
                      {layer.name}
                    </div>
                    <p className="text-[#E5E4E2]/70 font-body font-light text-sm leading-[1.7]">
                      {layer.body}
                    </p>
                  </div>
                ))}
              </div>

              <div className="pt-6 border-t border-[#C0C0C0]/40 flex flex-col sm:flex-row items-stretch sm:items-center gap-5">
                <p className="flex-1 text-[#E5E4E2]/70 font-body font-light text-sm sm:text-base leading-[1.75]">
                  Sovereign Node access opens in tandem with our upcoming public
                  initialization phase. Early institutional access is currently
                  reviewed through the Formal Intake channel below.
                </p>
                <a
                  href="#intake-section"
                  data-testid="portal-intake-link"
                  className="group inline-flex items-center justify-center gap-3 whitespace-nowrap border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors duration-500 text-xs uppercase tracking-[0.3em] font-body px-6 py-4 rounded-none sm:w-auto w-full"
                >
                  Route to Intake
                  <span
                    aria-hidden="true"
                    className="inline-block translate-x-0 group-hover:translate-x-1 transition-transform duration-500"
                  >
                    ↓
                  </span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 05 — Corporate Landscape & Compliance Guardrails */}
        <section
          data-testid="corporate-section"
          className="relative border-t border-[#C0C0C0]/25 px-6 sm:px-12 lg:px-20 py-24 sm:py-32"
        >
          <div className="max-w-7xl mx-auto">
            <div className="mb-14 max-w-3xl">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.5em] text-[#D4AF37] mb-6">
                ⟢ Section 05 · Compliance Guardrails
              </div>
              <h2
                data-testid="corporate-title"
                className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl text-white tracking-[-0.025em] leading-[1.05]"
              >
                Institutional Infrastructure{" "}
                <span className="text-[#D4AF37]">&amp; Security</span>.
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              {/* Executive Operations Block */}
              <div
                data-testid="executive-ops-block"
                className="relative border border-[#C0C0C0]/40 p-8 sm:p-10 bg-gradient-to-b from-[#070707] to-[#020202]"
              >
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-5">
                  ⟢ Executive Operations
                </div>
                <h3 className="font-heading font-medium text-xl sm:text-2xl lg:text-[1.6rem] text-white tracking-[-0.015em] leading-[1.2] mb-5">
                  Directed by Manager{" "}
                  <span className="text-[#D4AF37]">Ali Real Brown</span>.
                </h3>
                <p className="text-[#E5E4E2]/75 font-body font-light text-base leading-[1.75]">
                  Internal workflows, administrative reading, executive writing, and
                  strategic operational defense systems within our ecosystem are
                  permanently directed by Manager Ali Real Brown.
                </p>
                <div className="mt-6 pt-5 border-t border-[#C0C0C0]/40 flex items-center gap-3">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#E5E4E2]/55">
                    Custody · Permanent Assignment
                  </span>
                </div>
              </div>

              {/* Financial Firewall Block */}
              <div
                data-testid="financial-firewall-block"
                className="relative border border-[#C0C0C0]/40 p-8 sm:p-10 bg-gradient-to-b from-[#070707] to-[#020202]"
              >
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-5">
                  ⟢ Financial Firewall
                </div>
                <h3 className="font-heading font-medium text-xl sm:text-2xl lg:text-[1.6rem] text-white tracking-[-0.015em] leading-[1.2] mb-5">
                  Ironclad corporate firewall —{" "}
                  <span className="text-[#D4AF37]">$0 pre-revenue standard</span>.
                </h3>
                <p className="text-[#E5E4E2]/75 font-body font-light text-base leading-[1.75]">
                  To preserve the strict asset boundaries of our pre-revenue startup
                  baseline, all GoFundMe seed-stage capital, web donations, and app
                  revenues route directly into a dedicated, fee-free Bluevine Business
                  Checking Account. This creates an ironclad corporate firewall
                  completely separating development funds from personal household
                  metrics. Sophia Infinity operates at a protected $0 pre-revenue
                  startup standard.
                </p>
                <div className="mt-6 pt-5 border-t border-[#C0C0C0]/40 flex items-center gap-3">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#E5E4E2]/55">
                    Ledger · Bluevine Business Checking
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 06 — Formal Intake Gatekeeper */}
        <section
          data-testid="intake-section"
          id="intake-section"
          className="relative border-t border-[#C0C0C0]/25 px-6 sm:px-12 lg:px-20 py-24 sm:py-32 scroll-mt-24"
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mb-14">
              <div className="lg:col-span-6">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.5em] text-[#D4AF37] mb-6">
                  ⟢ Section 06 · Formal Intake Gatekeeper
                </div>
                <h2
                  data-testid="intake-title"
                  className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl text-white tracking-[-0.025em] leading-[1.05]"
                >
                  Secure Strategic{" "}
                  <span className="text-[#D4AF37]">Engagement</span>.
                </h2>
              </div>
              <div className="lg:col-span-6 lg:pt-3">
                <p className="text-[#E5E4E2]/70 text-base sm:text-lg leading-[1.75] max-w-xl">
                  We are currently opening select{" "}
                  <span className="text-white">Brand Deal Partnerships</span>,{" "}
                  <span className="text-white">corporate licensing alliances</span>, and{" "}
                  <span className="text-white">pre-seed capitalization tracks</span>.
                  Please select your category below to initiate the face-to-face Zoom
                  vetting process.
                </p>
              </div>
            </div>

            {/* Intake gate */}
            <IntakeGate />

            {/* Contact verification footer */}
            <div className="mt-16 pt-12 border-t border-[#C0C0C0]/40 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3">
                  ⟢ Official Staging Hub Registry
                </div>
                <a
                  data-testid="staging-hub-link"
                  href="https://sophia-infinity.com"
                  className="inline-block font-heading text-lg sm:text-xl lg:text-2xl text-white font-medium hover:text-[#D4AF37] transition-colors duration-500 tracking-[-0.015em] break-all"
                >
                  sophia-infinity.com
                </a>
              </div>
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3">
                  ⟢ Central Intake Portal
                </div>
                <a
                  data-testid="intake-email-link"
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="inline-block font-heading text-lg sm:text-xl lg:text-2xl text-white font-medium hover:text-[#D4AF37] transition-colors duration-500 tracking-[-0.015em] break-all"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3">
                  ⟢ Founder · LinkedIn
                </div>
                <a
                  data-testid="linkedin-link"
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 font-heading text-lg sm:text-xl lg:text-2xl text-white font-medium hover:text-[#D4AF37] transition-colors duration-500 tracking-[-0.015em] break-all"
                >
                  Ravenne Dame
                  <span
                    aria-hidden="true"
                    className="inline-block translate-x-0 group-hover:translate-x-1 transition-transform duration-500 text-[#D4AF37]"
                  >
                    ↗
                  </span>
                </a>
              </div>
            </div>

            {/* Addendum Metric */}
            <div
              data-testid="addendum-metric"
              className="mt-12 relative border border-[#C0C0C0]/40 bg-[#C0C0C0]/[0.05] p-7 sm:p-9"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/85">
                  ⌁ Addendum Metric
                </span>
                <span className="h-px flex-1 bg-[#C0C0C0]/40" />
                <span className="hidden sm:inline font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#E5E4E2]/45">
                  Launch Phase · Live
                </span>
              </div>
              <p className="text-[#E5E4E2]/70 font-body text-sm sm:text-base leading-[1.8]">
                All physical product distribution pipelines are officially closed for
                the launch phase. The platform is actively waiting for live delivery
                confirmation and tactical performance evaluation from our prominent
                private contract partner, with tracking metrics confirming arrival on
                or before this Friday, and an expected update rolling in over the next
                week or two. Our technical collaborator is currently reviewing all
                finalized system notes and active platform edits to determine if an
                updated presentation deck needs to be engineered to optimize our global
                enterprise pitches.
              </p>
            </div>
          </div>
        </section>

        {/* Distribution & Licensing Notice */}
        <section
          data-testid="distribution-notice"
          className="relative border-t border-[#C0C0C0]/25 px-6 sm:px-12 lg:px-20 py-16 sm:py-20"
        >
          <div className="max-w-5xl mx-auto">
            <div className="relative border border-[#D4AF37]/40 bg-[#C0C0C0]/[0.06] p-8 sm:p-10 lg:p-12">
              <span aria-hidden="true" className="absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-[#D4AF37]" />
              <span aria-hidden="true" className="absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-[#D4AF37]" />
              <span aria-hidden="true" className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-[#D4AF37]" />
              <span aria-hidden="true" className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-[#D4AF37]" />

              <div className="flex items-center gap-3 mb-6">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.45em] text-[#D4AF37]">
                  ⌁ Distribution &amp; Licensing Notice
                </span>
                <span className="h-px flex-1 bg-[#D4AF37]/25" />
              </div>

              <h2 className="font-heading font-medium text-2xl sm:text-3xl lg:text-[2rem] text-white tracking-[-0.015em] leading-[1.2] mb-6">
                All disclosures, prototypes, and produced materials are bound under
                strict <span className="text-[#D4AF37]">NDA &amp; contractual instruments</span>.
              </h2>

              <div className="space-y-5 text-[#E5E4E2]/80 font-body font-light text-base sm:text-lg leading-[1.8]">
                <p>
                  Sophia Infinity does not currently hold any public retail,
                  wholesale, or consumer-distribution licensing. Accordingly, any
                  material, prototype, formulation, schematic, deliverable, document,
                  or proprietary asset — whether physical or digital, partial or
                  complete — that is produced, transmitted, demonstrated, or
                  distributed to any individual, entity, or partner is released
                  exclusively under a fully executed Mutual Non-Disclosure Agreement
                  (NDA) and binding contractual instrument.
                </p>
                <p>
                  No element of our intellectual property, inventions, or operational
                  systems may be reproduced, referenced, transferred, or commercially
                  utilized outside the explicit scope of those agreements until
                  formal licensing authorization is established and recorded in
                  writing.
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-[#C0C0C0]/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#E5E4E2]/55">
                  Enforcement · Active &amp; Ongoing
                </span>
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]/80">
                  Effective Immediately · All Channels
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer
          data-testid="site-footer"
          className="relative border-t border-[#C0C0C0]/25 px-6 sm:px-12 lg:px-20 py-12"
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#E5E4E2]/35">
              © {new Date().getFullYear()} Sophia Infinity · All Rights Reserved
            </div>
            <div className="flex items-center gap-6">
              <Link
                to="/about-ravenne"
                data-testid="footer-sanctuary-link"
                className="text-[10px] uppercase tracking-[0.4em] text-[#E5E4E2]/45 hover:text-[#D4AF37] transition-colors"
              >
                The Sanctuary
              </Link>
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#E5E4E2]/35">
                Confidential · Pre-Launch
              </span>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}

function IntakeGate() {
  const [selected, setSelected] = useState(INTAKE_OPTIONS[0]);

  return (
    <div
      data-testid="intake-gate"
      className="relative border border-[#D4AF37]/35 bg-gradient-to-b from-[#070707] to-[#020202] p-8 sm:p-10 lg:p-12"
    >
      <span aria-hidden="true" className="absolute -top-px -left-px h-3 w-3 border-t-2 border-l-2 border-[#D4AF37]" />
      <span aria-hidden="true" className="absolute -top-px -right-px h-3 w-3 border-t-2 border-r-2 border-[#D4AF37]" />
      <span aria-hidden="true" className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-[#D4AF37]" />
      <span aria-hidden="true" className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-[#D4AF37]" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-7">
          <label
            htmlFor="intake-category"
            className="block font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3"
          >
            ⟢ Engagement Category
          </label>
          <div className="relative">
            <select
              id="intake-category"
              data-testid="intake-category-select"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full bg-transparent border-b border-[#D4AF37]/40 focus:border-[#D4AF37] outline-none text-white font-heading font-medium text-lg sm:text-xl lg:text-2xl py-3 transition-colors duration-300 appearance-none cursor-pointer tracking-[-0.015em]"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14' fill='none'><path d='M2 5 L7 10 L12 5' stroke='%23D4AF37' stroke-width='1.4' fill='none'/></svg>\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 0.25rem center",
                paddingRight: "2rem",
              }}
            >
              {INTAKE_OPTIONS.map((opt) => (
                <option key={opt} value={opt} className="bg-[#050505] text-white">
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-3 font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#E5E4E2]/45">
            Initiates a face-to-face Zoom vetting process under mutual NDA.
          </div>
        </div>

        <div className="lg:col-span-5">
          <ContactSheet
            defaultInquiry={selected}
            trigger={
              <button
                type="button"
                data-testid="intake-initiate-btn"
                className="group inline-flex w-full items-center justify-center gap-3 bg-white text-black hover:bg-[#D4AF37] hover:text-black transition-colors duration-500 text-xs sm:text-sm uppercase tracking-[0.3em] font-body px-8 py-5 rounded-none"
              >
                Initiate Engagement
                <span
                  aria-hidden="true"
                  className="inline-block translate-x-0 group-hover:translate-x-1 transition-transform duration-500"
                >
                  →
                </span>
              </button>
            }
          />
        </div>
      </div>
    </div>
  );
}
